import { toast as sonner } from 'sonner';
import { track } from './analytics';

type Sonner = typeof sonner;

// Toast text can carry provider or API detail (names, account ids), so only a
// non-reversible fingerprint leaves the browser: the same message always maps
// to the same code, which is enough to rank errors. FNV-1a, 32-bit.
function errorCode(message: unknown): string {
  if (typeof message !== 'string') return 'unknown';
  let hash = 0x811c9dc5;
  for (let i = 0; i < message.length; i++) {
    hash ^= message.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `e_${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function trackErrorShown(message: unknown) {
  try {
    track('error_shown', { error_code: errorCode(message) });
  } catch {
    // tracking must never stop the toast
  }
}

const error = (...args: Parameters<Sonner['error']>) => {
  trackErrorShown(args[0]);
  return sonner.error(...args);
};

type PromiseData = NonNullable<Parameters<Sonner['promise']>[1]>;

// Sonner resolves `error` only when the promise rejects, so tracking there fires once per shown error.
const promise = <T>(p: Parameters<Sonner['promise']>[0], data?: PromiseData) => {
  if (!data?.error) return sonner.promise<T>(p as never, data);
  const onError = data.error;
  return sonner.promise<T>(
    p as never,
    {
      ...data,
      error: (err: unknown) => {
        const shown = typeof onError === 'function' ? onError(err) : onError;
        const message =
          typeof shown === 'object' && shown && 'message' in shown ? shown.message : shown;
        trackErrorShown(message);
        return shown;
      },
    } as PromiseData
  );
};

// Reads `sonner` on every access so test suites that mock it process-wide still see their mock.
export const toast = new Proxy((() => {}) as unknown as Sonner, {
  get: (_, prop) =>
    prop === 'error' ? error : prop === 'promise' ? promise : Reflect.get(sonner, prop),
  apply: (_, __, args: Parameters<Sonner>) => sonner(...args),
}) as Sonner;
